import clsx from "clsx";
import { formatCompact, formatExact, isCompacted } from "../../utils/format-number";
import ValueTooltip from "./value-tooltip";

type Props = {
  value: unknown;
  currency?: boolean;
  className?: string;
  /** Suffix rendered inside the tooltip after the exact figure (e.g. "chases"). */
  unit?: string;
};

/**
 * A figure shown compact, with the exact value on hover.
 *
 * Only wraps in a tooltip when compaction actually hid something — a value that
 * renders identically either way gets no cursor affordance, so the dotted underline
 * always means "there is more precision here".
 *
 * Proportional (not tabular) figures: these are standalone display numbers, and
 * tabular-nums makes them look gappy at large sizes. Columns of numbers opt into
 * tabular separately.
 */
export default function StatNumber({ value, currency, className, unit }: Props) {
  const compact = formatCompact(value, currency);

  if (!isCompacted(value, currency)) {
    return <span className={className}>{compact}</span>;
  }

  return (
    <ValueTooltip
      label={
        <>
          {formatExact(value, currency)}
          {unit ? <span className="text-light-gray font-medium"> {unit}</span> : null}
        </>
      }
      className={clsx("decoration-edge-strong", className)}
    >
      {compact}
    </ValueTooltip>
  );
}
